import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { getCountryCallingCode } from 'react-phone-number-input';
import { toast } from 'react-toastify';
import { compose } from 'recompose';
import { Input, PhoneInput } from 'components/FormFields';
import Loading from 'components/Loading';
import SearchUsers from 'pages/track/Employees/gql/queries/search-users';
import AddMember from '../../../AddMember/gql/AddMember';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalDescription,
  StepIndicator,
  Step,
  StepNumber,
  StepLabel,
  StepDivider,
  FormGroup,
  FormRow,
  SearchResultsList,
  SearchResultItem,
  SearchResultName,
  SearchResultPhone,
  NoResultsMessage,
  SelectedUserCard,
  SelectedUserName,
  SelectedUserDetail,
  FormButton,
  ClearButton,
  FooterActions,
} from './StyledComponents';

const platform =
  'tickethoss' === process.env.REACT_APP_PLATFORM
    ? 'Ticket Hoss'
    : 'pitpay' === process.env.REACT_APP_PLATFORM
    ? 'Pit Pay'
    : 'Kart Pass';

// Icons
const CloseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const AddStaffModal = ({
  isOpen,
  onClose,
  accountId,
  onSuccess,
  addMember,
  data: searchData,
}) => {
  const [step, setStep] = useState(1); // 1 = search, 2 = create new
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showNoResults, setShowNoResults] = useState(false);

  const resetState = () => {
    setStep(1);
    setSearchValue('');
    setSearchResults([]);
    setSelectedUser(null);
    setShowNoResults(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handlePhoneSearch = async (value) => {
    setSearchValue(value);
    setShowNoResults(false);

    // Only search if phone number is complete enough (10+ digits)
    if (value && value.replace(/\D/g, '').length >= 10) {
      setIsSearching(true);
      try {
        const response = await searchData.refetch({ query: value });
        if (response.data.searchUsers && response.data.searchUsers.length > 0) {
          setSearchResults(response.data.searchUsers);
          setShowNoResults(false);
        } else {
          setSearchResults([]);
          setShowNoResults(true);
        }
      } catch (error) {
        setSearchResults([]);
        setShowNoResults(true);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchResults([]);
    setShowNoResults(false);
  };

  const handleCreateNew = () => {
    setStep(2);
  };

  const handleAddExistingUser = async () => {
    if (!selectedUser) return;

    try {
      const response = await addMember({
        user_id: selectedUser.id,
        account_id: parseInt(accountId),
      });

      if (!response || response.errors) {
        toast.error('Error adding staff member');
      } else {
        toast.success('Staff member added successfully');
        onSuccess(response.data.addMember);
        handleClose();
      }
    } catch (error) {
      toast.error('Error adding staff member');
    }
  };

  const initialValues = {
    first_name: '',
    last_name: '',
    cellphone: searchValue || '',
    country_code: 'US',
    calling_code: '1',
    email: '',
  };

  const validate = (values) => {
    const errors = {};
    if (!values.first_name) {
      errors.first_name = 'First name is required';
    }
    if (!values.last_name) {
      errors.last_name = 'Last name is required';
    }
    if (!values.cellphone) {
      errors.cellphone = 'Phone number is required';
    }
    return errors;
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setSubmitting(true);

    try {
      const response = await addMember({
        first_name: values.first_name,
        last_name: values.last_name,
        cellphone: values.calling_code
          ? values.cellphone.replace(`+${values.calling_code}`, '')
          : values.cellphone.replace(`+1`, ''),
        calling_code: values.calling_code || '1',
        country_code: values.country_code || 'US',
        email: values.email || undefined,
        account_id: parseInt(accountId),
      });

      if (!response || response.errors) {
        toast.error('Error creating staff member');
        if (response?.errors) {
          setErrors(response.errors);
        }
      } else {
        toast.success('Staff member created successfully');
        onSuccess(response.data.addMember);
        handleClose();
      }
    } catch (error) {
      toast.error('Error creating staff member');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>Add Staff Member</ModalTitle>
          <CloseButton type="button" onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <StepIndicator>
            <Step>
              <StepNumber active={step === 1} completed={step > 1}>
                {step > 1 ? '✓' : '1'}
              </StepNumber>
              <StepLabel active={step === 1}>Search</StepLabel>
            </Step>
            <StepDivider />
            <Step>
              <StepNumber active={step === 2}>2</StepNumber>
              <StepLabel active={step === 2}>Details</StepLabel>
            </Step>
          </StepIndicator>

          {step === 1 && (
            <>
              <ModalDescription>
                Enter a phone number to search for an existing {platform} user.
                If they're not in the system, you can create a new staff member.
              </ModalDescription>

              {selectedUser ? (
                <SelectedUserCard>
                  <SelectedUserName>
                    {selectedUser.first_name} {selectedUser.last_name}
                  </SelectedUserName>
                  <SelectedUserDetail>
                    {selectedUser.cellphone}
                  </SelectedUserDetail>
                  {selectedUser.email && (
                    <SelectedUserDetail>
                      {selectedUser.email}
                    </SelectedUserDetail>
                  )}
                  <ClearButton
                    type="button"
                    onClick={() => setSelectedUser(null)}
                  >
                    Search for someone else
                  </ClearButton>
                </SelectedUserCard>
              ) : (
                <>
                  <FormGroup>
                    <PhoneInput
                      label="Phone Number"
                      placeholder="Enter phone number to search"
                      value={searchValue}
                      onChange={handlePhoneSearch}
                    />
                  </FormGroup>

                  {isSearching && (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                      <Loading size={30} />
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <SearchResultsList>
                      {searchResults.map((user) => (
                        <SearchResultItem
                          key={user.id}
                          onClick={() => handleSelectUser(user)}
                        >
                          <SearchResultName>
                            {user.first_name} {user.last_name}
                          </SearchResultName>
                          <SearchResultPhone>
                            {user.cellphone}
                          </SearchResultPhone>
                        </SearchResultItem>
                      ))}
                    </SearchResultsList>
                  )}

                  {showNoResults && !isSearching && (
                    <NoResultsMessage>
                      No existing user found with this phone number.
                      <br />
                      <br />
                      <FormButton
                        type="button"
                        variant="primary"
                        onClick={handleCreateNew}
                      >
                        Create New Staff Member
                      </FormButton>
                    </NoResultsMessage>
                  )}
                </>
              )}
            </>
          )}

          {step === 2 && (
            <Formik
              initialValues={initialValues}
              validate={validate}
              onSubmit={handleSubmit}
              validateOnChange={false}
              validateOnBlur={false}
            >
              {({
                values,
                errors,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <ModalDescription>
                    Fill out the details below to create a new staff member.
                  </ModalDescription>

                  <FormGroup>
                    <PhoneInput
                      label="Phone Number"
                      placeholder="Enter phone number"
                      value={values.cellphone}
                      onChange={(value) => setFieldValue('cellphone', value)}
                      onCountryChange={(e) => {
                        if (e) {
                          setFieldValue('country_code', e);
                          setFieldValue(
                            'calling_code',
                            getCountryCallingCode(e)
                          );
                        }
                      }}
                      error={errors.cellphone}
                      required
                    />
                  </FormGroup>

                  <FormRow>
                    <FormGroup>
                      <Input
                        label="First Name"
                        placeholder="Enter first name"
                        name="first_name"
                        value={values.first_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.first_name}
                        required
                      />
                    </FormGroup>
                    <FormGroup>
                      <Input
                        label="Last Name"
                        placeholder="Enter last name"
                        name="last_name"
                        value={values.last_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.last_name}
                        required
                      />
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <Input
                      label="Email"
                      placeholder="Enter email address (optional)"
                      name="email"
                      type="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.email}
                    />
                  </FormGroup>

                  <FooterActions style={{ margin: '24px -24px -24px -24px' }}>
                    <FormButton type="button" onClick={() => setStep(1)}>
                      Back
                    </FormButton>
                    <FormButton
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Creating...' : 'Create Staff Member'}
                    </FormButton>
                  </FooterActions>
                </form>
              )}
            </Formik>
          )}
        </ModalBody>

        {step === 1 && selectedUser && (
          <FooterActions>
            <FormButton type="button" onClick={handleClose}>
              Cancel
            </FormButton>
            <FormButton
              type="button"
              variant="primary"
              onClick={handleAddExistingUser}
            >
              Add Staff Member
            </FormButton>
          </FooterActions>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};

AddStaffModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuccess: PropTypes.func.isRequired,
  addMember: PropTypes.func.isRequired,
  data: PropTypes.object,
};

export default compose(SearchUsers, AddMember)(AddStaffModal);
